import React, { useState, useEffect, useRef } from "react";
import { FormControl, InputLabel, Button, Select, Typography, MenuItem, Alert, TextField, IconButton, Tooltip, Box, Divider, Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { useAppSnackbar } from '../../contexts/AppSnackbarContext';
import { useAppConfirm } from '../../contexts/AppConfirmContext';
import MultiEmailInput from "../MultiEmailInput";
import HtmlInput from "../HtmlInput";

const EMAIL_OR_VAR_REGEX =
    /^(?:[^\s@]+@[^\s@]+\.[^\s@]{2,}|@\{[a-zA-Z_][\w.]*\}|\$\{[a-zA-Z_][\w.]*\})$/;

const CONNECTION_TYPES = [
    "DB",
    "API",
    "SFTP",
    "EMAIL"
];

const STEPS = [
    { label: 'API GET', value: 'Workflow.Steps.Connections.ApiGetStep' },
    { label: 'API POST OBTAIN DOCUMENT', value: 'Workflow.Steps.Connections.ApiPostDocumentStep' },
    { label: 'API POST', value: 'Workflow.Steps.Connections.ApiPostStep' },
    { label: 'API PUT', value: 'Workflow.Steps.Connections.ApiPutStep' },
    { label: 'DB READ', value: 'Workflow.Steps.Connections.DbReadStep' },
    { label: 'DB WRITE', value: 'Workflow.Steps.Connections.DbWriteStep' },
    { label: 'SFTP Placholder', value: 'Workflow.Steps.Connections.SftpStep' },
    { label: 'EMAIL Send', value: 'Workflow.Steps.Connections.EmailSendStep' },
];

export default function ActionForm() {
    const { setRefreshTree } = useOutletContext();
    const [step, setStep] = useState('none');
    const [conType, setConType] = useState('API');
    const [actionForm, setActionForm] = useState({ data: '{}' });
    const [validationError, setValidationError] = useState({});
    const [showError, setShowError] = useState(false);
    const [isLoading, setIsLoading] = useState(false)
    const { connectionId, actionId } = useParams();
    const [id, setId] = useState(null);
    const [data, setData] = useState(JSON.parse(actionForm.data ?? '{}'));
    const [dataInput, setDataInput] = useState(actionForm.data ?? '{}');
    const [toEmails, setToEmails] = useState([]);
    const [ccEmails, setCCEmails] = useState([]);
    const [bccEmails, setBCCEmails] = useState([]);

    const bodyRef = useRef(null);

    const [isEditMode, setIsEditMode] = useState(actionId === undefined || actionId === "new");
    const { showSnackbar } = useAppSnackbar();
    const { confirm } = useAppConfirm();
    const navigate = useNavigate();

    async function onSubmit(action) {
        try {
            setIsLoading(true);
            const url = actionId ? `/api/connection/${connectionId}/actions/${actionId}` : `/api/connection/${connectionId}/actions`;
            const response = await fetch(url, {
                method: actionId ? 'PUT' : 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(action)
            });

            if (response.ok) {
                showSnackbar(actionId ? "Action updated successfully!" : "Action created successfully!", "success");
                setRefreshTree(prev => prev + 1);
                setIsEditMode(false);
                if (!actionId) {
                    // After create, redirect to edit page
                    const data = await response.json();
                    //console.log(data);
                    navigate(`/connections/${connectionId}/actions/${data.id}/edit`, { replace: true });
                }
            } else {
                const errorData = await response.json();
                showSnackbar("Failed to save action", "error");
                setValidationError({ general: "Failed to save action" });
                setShowError(true);
            }
        } catch (error) {
            showSnackbar("Network error occurred", "error");
            setValidationError({ general: "Network error occurred" });
            setShowError(true);
        } finally {
            setIsLoading(false);
        }
    }

    // Initial Effect
    useEffect(() => {
        let isMounted = true;

        async function fetchData() {
            try {
                setIsLoading(true);
                if (connectionId == null) return;
                const resp2 = await fetch(`/api/connection/${connectionId}`, { credentials: 'include' });
                let conn = await resp2.json();
                setConType(CONNECTION_TYPES[conn.type] ?? 'API');
                if (actionId) {
                    const resp = await fetch(`/api/connection/${connectionId}/actions/${actionId}`, { credentials: 'include' });
                    let act = await resp.json();
                    setStep(act.method ?? 'none');
                    setDataInput(act.data ?? '{}');
                    if (isMounted) setActionForm(act);

                    setIsEditMode(false);
                } else {
                    setActionForm({ data: '{}' });
                    setIsEditMode(true);
                }
                setId(actionId ?? "new");
            } catch (e) {
                console.log(e);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        }
        fetchData();
        return () => { isMounted = false; };
    }, [connectionId, actionId]);

    useEffect(() => {
        if (bodyRef.current == null) return;
        let x = bodyRef.current.getElementsByTagName('textarea')[0];
        let parsed = JSON.parse(actionForm.data ?? '{}');
        x.value = ((parsed.body != null && typeof parsed.body === 'object') ? JSON.stringify(parsed.body, null, 2) : parsed.body) ?? '';
    }, [bodyRef.current]);

    useEffect(() => {
        try {
            let parsed = JSON.parse(actionForm.data ?? '{}');
            if (conType == 'EMAIL') {
                if (parsed.body == null || parsed.body.trim() == '') parsed.body = '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">\n<div>\n    <h1>Hello World</h1>\n    <p>This is a sample email body.</p>\n</div>';
            }
            setData(parsed);
            if (conType == 'EMAIL') {
                setToEmails(parsed.to ?? []);
                setCCEmails(parsed.cc ?? []);
                setBCCEmails(parsed.bcc ?? []);
            }
        } catch (e) {
            // Ignore JSON parse errors here
        }
    }, [actionForm.data]);

    const handleInputChange = (name, value, required) => {
        if (required) {
            if (value.trim() === "") {
                setValidationError((prev) => ({
                    ...prev,
                    [`${name}-${id}`]: `${name.charAt(0).toUpperCase() + name.slice(1)} is required`
                }));
                setShowError(true);
            } else {
                setValidationError((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[`${name}-${id}`];
                    return newErrors;
                });
                setShowError(false);
            }
        }
        setActionForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    const handleDataInputChange = (value) => {
        setDataInput(value);
        try {
            JSON.parse(value);
            setActionForm((prev) => ({
                ...prev,
                data: value,
            }));
            setValidationError((prev) => {
                const newErrors = { ...prev };
                delete newErrors[`data-${id}`];
                return newErrors;
            });
            setShowError(false);
        } catch (e) {
            setValidationError((prev) => ({
                ...prev,
                [`data-${id}`]: "Config must be valid JSON"
            }));
            setShowError(true);
        }
    }

    const handleDataPorpertyChange = (name, value, validate = 0) => {
        if (validate == 1) {
            try {
                value = JSON.parse(value);
                setValidationError((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[`${name}-${id}`];
                    return newErrors;
                });
            }
            catch (e) {
                setValidationError((prev) => ({
                    ...prev,
                    [`${name}-${id}`]: `${name.charAt(0).toUpperCase() + name.slice(1)} must be valid JSON`
                }));
            }
        }

        if (validate == 2) {
            if (value.trim() === "") {
                setValidationError((prev) => ({
                    ...prev,
                    [`${name}-${id}`]: `${name.charAt(0).toUpperCase() + name.slice(1)} is required`
                }));
            } else {
                setValidationError((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[`${name}-${id}`];
                    return newErrors;
                });
            }
        }

        let newData = { ...data, [name]: value };
        setActionForm((prev) => ({
            ...prev,
            data: JSON.stringify(newData, null, 2),
        }));
    }

    const handleStepChange = (event) => {
        const value = event.target.value;
        setStep(value);
        setActionForm((prev) => ({
            ...prev,
            method: value,
            namespace: 'Workflow'
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setShowError(false);

        if (Object.keys(validationError).length > 0) {
            setShowError(true);
            return;
        } else {
            setValidationError([]);
            setShowError(false);
        }

        onSubmit(actionForm);
    };

    const handleEmailsChange = (type, emails) => {
        console.log(emails.filter(e => EMAIL_OR_VAR_REGEX.test(e)).map(e => e.trim()))
        let newData = { ...data };
        if (emails.length > 0) {
            newData[type] = emails.filter(e => EMAIL_OR_VAR_REGEX.test(e)).map(e => e.trim());
        } else {
            delete newData[type];
        }
        setData(newData);
        setActionForm((prev) => ({
            ...prev,
            data: JSON.stringify(newData, null, 2),
        }));
    }

    const handleEditClick = () => setIsEditMode(true);

    const handleCancelEdition = async () => {
        if (actionId && actionForm) {
            const confirmed = await confirm({
                title: 'Cancel Changes',
                content: 'Are you sure you want to cancel? Unsaved changes will be lost.',
                confirmText: 'Confirm',
                cancelText: 'Cancel'
            });
            if (confirmed) {
                // Re-fetch to reset form
                setIsLoading(true);
                const resp = await fetch(`/api/connection/${connectionId}/actions/${actionId}`, { credentials: 'include' });
                let act = await resp.json();
                setActionForm(act);
                setIsEditMode(false);
                setIsLoading(false);
            }
        } else {
            setIsEditMode(false);
        }
    };

    const handleDelete = async () => {
        const confirmed = await confirm({
            title: 'Delete Action',
            content: 'Are you sure you want to delete this action? This process will be permanent.',
            confirmText: 'Delete',
            cancelText: 'Cancel'
        });
        if (confirmed) {
            setIsLoading(true);
            try {
                const resp = await fetch(`/api/connection/actions/${actionId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (resp.ok) {
                    showSnackbar("Action deleted successfully!", "success");
                    setRefreshTree(prev => prev + 1);
                    navigate('/connections', { replace: true });
                } else if (resp.status === 409) {
                    const body = await resp.text();
                    showSnackbar(body, 'error');
                } else if (resp.status === 404) {
                    showSnackbar("Action not found. It may have already been deleted.", "error");
                    navigate('/connections', { replace: true });
                }
            } catch {
                showSnackbar("An error occurred while deleting the action.", "error");
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div style={{ width: '100%', padding: 20 }}>
            {showError && validationError && Object.keys(validationError).length > 0 && (
                <Alert severity="error" style={{ marginBottom: 20 }}>
                    {Object.values(validationError).map((error, index) => (
                        <div key={index}>{error}</div>
                    ))}
                </Alert>
            )}
            <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Typography variant="h6">{id === 'new' ? 'Create New Action' : (actionForm.name || 'Edit Action')}</Typography>
                    <div>
                        {!isEditMode && (
                            <Tooltip title="Edit">
                                <IconButton onClick={handleEditClick} color="primary">
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {isEditMode && (
                            <>

                                <Box display="flex" gap={1} flexDirection="row">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        startIcon={<SaveIcon />}
                                        disabled={isLoading}
                                    >
                                        {actionId === 'new' ? 'Create Action' : 'Save Changes'}
                                    </Button>
                                    {actionId && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={handleDelete}
                                            disabled={isLoading}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    {actionId && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancelEdition}
                                            disabled={isLoading}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                </Box>
                            </>
                        )}
                    </div>
                </div>
                <Divider sx={{ p: 2 }}></Divider>
                <FormControl fullWidth margin="normal">
                    <TextField
                        error={validationError[`name-${id}`] != null}
                        helperText={validationError[`name-${id}`]}
                        id={`name-${id}`}
                        label="Action Name"
                        onChange={(e) => handleInputChange('name', e.target.value, true)}
                        variant={isEditMode ? "outlined" : "filled"}
                        name={`name-${id}`}
                        value={actionForm.name ?? ""}
                        InputProps={{ readOnly: !isEditMode }}
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <TextField
                        error={validationError[`description-${id}`] != null}
                        helperText={validationError[`description-${id}`]}
                        label="Description"
                        id={`description-${id}`}
                        name={`description-${id}`}
                        type="text"
                        value={actionForm.description ?? ''}
                        multiline
                        minRows={4}
                        maxRows={10}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        variant={isEditMode ? "outlined" : "filled"}
                        InputProps={{ readOnly: !isEditMode }}
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <InputLabel id={`step-label-${id}`}>Type</InputLabel>
                    <Select
                        error={validationError[`step-select-${id}`] != null}
                        labelId={`step-label-${id}`}
                        id={`step-select-${id}`}
                        value={step ?? 'none'}
                        label="Step"
                        onChange={handleStepChange}
                        disabled={!isEditMode}
                        variant={isEditMode ? "outlined" : "filled"}
                    >
                        <MenuItem value='none' disabled>Step</MenuItem>
                        {STEPS.filter(f => f.label.includes(conType)).map((step) => <MenuItem key={step.value} value={step.value}>{step.label}</MenuItem>)}
                    </Select>
                </FormControl>
                <Alert severity="info">
                    To include <b>Payload</b> variables use the format: <b>@{"{variable.path}"}</b> <br />
                    To include <b>Last Step</b> variables use the format: <b>${"{variable.path}"}</b>
                </Alert>
                {STEPS.filter(f => f.label.includes('API')).map(f => f.value).includes(actionForm.method) && (
                    <FormControl fullWidth margin="normal">
                        <TextField
                            error={validationError[`url-${id}`] != null}
                            helperText={validationError[`url-${id}`]}
                            id={`url-${id}`}
                            label="API Route"
                            onChange={(e) => handleDataPorpertyChange('url', e.target.value, 2)}
                            variant={isEditMode ? "outlined" : "filled"}
                            name={`url-${id}`}
                            defaultValue={data.url ?? ''}
                            type="text"
                            fullWidth
                            margin="normal"
                            InputProps={{ readOnly: !isEditMode }}
                        />
                    </FormControl>
                )}
                {STEPS.filter(f => f.label.includes('API') && f.label != 'API GET').map(f => f.value).includes(actionForm.method) && (
                    <FormControl fullWidth margin="normal">
                        <TextField
                            ref={bodyRef}
                            error={validationError[`body-${id}`] != null}
                            helperText={validationError[`body-${id}`]}
                            id={`body-${id}`}
                            label="Request body"
                            onChange={(e) => handleDataPorpertyChange('body', e.target.value, 1)}
                            variant={isEditMode ? "outlined" : "filled"}
                            name={`body-${id}`}
                            defaultValue={((data.body != null && typeof data.body === 'object') ? JSON.stringify(data.body, null, 2) : data.body) ?? ''}
                            type="text"
                            multiline
                            minRows={4}
                            maxRows={10}
                            fullWidth
                            margin="normal"
                            InputProps={{ readOnly: !isEditMode }}
                        />
                    </FormControl>
                )}
                {STEPS.filter(f => f.label.includes('DB')).map(f => f.value).includes(actionForm.method) && (
                    <FormControl fullWidth margin="normal" >
                        <TextField
                            error={validationError[`query-${id}`] != null}
                            helperText={validationError[`query-${id}`]}
                            id={`query-${id}`}
                            label="Raw Query"
                            onChange={(e) => handleDataPorpertyChange('query', e.target.value, 2)}
                            variant={isEditMode ? "outlined" : "filled"}
                            name={`query-${id}`}
                            defaultValue={data.query ?? ''}
                            type="text"
                            multiline
                            minRows={4}
                            maxRows={10}
                            fullWidth
                            margin="normal"
                            InputProps={{ readOnly: !isEditMode }}
                        />
                    </FormControl>
                )}
                {!['API', 'DB', 'EMAIL'].includes(conType) && (
                    <FormControl fullWidth margin="normal" >
                        <TextField
                            error={validationError[`data-${id}`] != null}
                            helperText={validationError[`data-${id}`]}
                            id={`data-${id}`}
                            label="Config"
                            onChange={(e) => handleDataInputChange(e.target.value)}
                            variant={isEditMode ? "outlined" : "filled"}
                            name={`data-${id}`}
                            value={dataInput ?? "{}"}
                            multiline
                            minRows={4}
                            maxRows={10}
                            InputProps={{ readOnly: !isEditMode }}
                        />
                    </FormControl>
                )}
                {STEPS.filter(f => f.label.includes('EMAIL')).map(f => f.value).includes(actionForm.method) && (
                    <div style={{ marginTop: 16 }}>
                        <MultiEmailInput
                            label="To:"
                            value={toEmails}
                            onChange={(emails) => handleEmailsChange('to', emails)}
                            variant={isEditMode ? "outlined" : "filled"}
                            InputProps={{ readOnly: !isEditMode }}
                            allowDelete={isEditMode}
                        />
                        <MultiEmailInput
                            label="CC:"
                            value={ccEmails}
                            onChange={(emails) => handleEmailsChange('cc', emails)}
                            variant={isEditMode ? "outlined" : "filled"}
                            InputProps={{ readOnly: !isEditMode }}
                            allowDelete={isEditMode}
                        />
                        <MultiEmailInput
                            label="BCC:"
                            value={bccEmails}
                            onChange={(emails) => handleEmailsChange('bcc', emails)}
                            variant={isEditMode ? "outlined" : "filled"}
                            InputProps={{ readOnly: !isEditMode }}
                            allowDelete={isEditMode}
                        />
                        <Typography variant="caption" sx={{ mt: 0.5, display: "block", opacity: 0.8 }}>
                            You can use dynamic placeholders like <code>@{"{userEmail}"}</code> or{" "}
                            <code>${"{userEmail}"}</code> in the emails too.
                        </Typography>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                error={validationError[`subject-${id}`] != null}
                                helperText={validationError[`subject-${id}`]}
                                id={`subject-${id}`}
                                label="Email Subject"
                                onChange={(e) => handleDataPorpertyChange('subject', e.target.value, 2)}
                                variant={isEditMode ? "outlined" : "filled"}
                                name={`subject-${id}`}
                                defaultValue={data.subject ?? ''}
                                type="text"
                                fullWidth
                                margin="normal"
                                InputProps={{ readOnly: !isEditMode }}
                            />
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <Stack spacing={2}>
                                <HtmlInput
                                    value={data.body ?? ''}
                                    onChange={(value) => handleDataPorpertyChange('body', value, 0)}
                                    label="Email HTML Template"
                                    disabled={!isEditMode}
                                />
                            </Stack>
                        </FormControl>
                    </div>
                )}
            </form>

            {STEPS.filter(f => f.label.includes('EMAIL')).map(f => f.value).includes(actionForm.method) && (
                <div>
                    <hr />
                    <Typography variant="h6" gutterBottom>
                        Preview body
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        This is a preview of the email body. Dynamic variables will be replaced during execution.
                    </Typography>
                    <Box
                        sx={{
                            p: 2,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            backgroundColor: '#f9f9f9',
                            minHeight: '100px',
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                        }}
                        dangerouslySetInnerHTML={{ __html: data.body ?? '' }}
                    />
                </div>
            )}

        </div >
    );
}

